import React, { useEffect, useRef, useState } from 'react'
import {
    Box,
    Flex,
    Icon,
    SimpleGrid,
    FormControl,
    FormLabel,
    Input,
    useColorModeValue,
    Spinner,
    Select,
    Text
} from "@chakra-ui/react"

import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"

import MiniStatistics from "components/card/MiniStatistics"
import AnimatedNumber from "components/card/AnimatedNumber"
import IconBox from "components/icons/IconBox"
import CheckTable from "views/admin/default/components/CheckTable"
import {
    MdAttachMoney,
    MdBarChart,
    MdFileCopy, MdSwapHoriz, MdTrendingUp
} from "react-icons/md"
import axiosInstance from "../../../api/axiosInstance"
import { formatNumber } from "../../../Utils/Utils"
import { useNotification } from "../../../contexts/NotificationContext"
import dayjs from "dayjs"
import { formatInTimeZone } from 'date-fns-tz'
import DailyTraffic from "./DailyTraffic";
import TotalSpent from "./TotalSpent";
import RoasPerDay from "./RoasPerDay";
import TrafficByProduct from "./TrafficByProduct";
import PieCard from "./PieCard";
import BestSellingOptionsChart from "./BestSellingOption";
import {createColumnHelper} from "@tanstack/react-table";
import UpsellsList from "./components/UpsellsList";
import isoWeek from 'dayjs/plugin/isoWeek'
import { useTranslation } from 'react-i18next'

const Dashboard = () => {
    const { t } = useTranslation()
    const tr = (key, defaultValue) => t(key, { defaultValue })
    const { addErrorNotifications } = useNotification()
    const [loading, setLoading] = useState(true)
    const [metrics, setMetrics] = useState({})
    const [roasPerDayMetrics, setRoasPerDayMetrics] = useState({
        data: [],
        growthPercentage: 0,
    })
    const [startDate, setStartDate] = useState(dayjs().startOf('day'))
    const [endDate, setEndDate] = useState(dayjs().endOf('day'))
    const [period, setPeriod] = useState('yesterday')
    const [selectedRange, setSelectedRange] = useState('Today')
    const [roasLoading, setRoasLoading] = useState(false)
    const [roasChartDateRange, setRoasChartDateRange] = useState({
        startDate: dayjs().subtract(6, 'day').startOf('day'),
        endDate: dayjs().endOf('day'),
    })
    const timeZone = 'Europe/Bucharest'
    const hasLoadedInitialRoasRange = useRef(false)
    dayjs.extend(isoWeek)

    useEffect(() => {
        fetchMetrics(startDate, endDate)
    }, [startDate, endDate])

    // Silent auto-refresh every 60 s, only when viewing today's live data
    useEffect(() => {
        if (selectedRange !== 'Today') return
        const id = setInterval(() => fetchMetrics(startDate, endDate, true), 60000)
        return () => clearInterval(id)
    }, [selectedRange, startDate, endDate])

    const buildDateParams = (start, end) => ({
        start_date: start ? formatInTimeZone(start.toDate(), timeZone, 'yyyy-MM-dd') : undefined,
        end_date: end ? formatInTimeZone(end.toDate(), timeZone, 'yyyy-MM-dd') : undefined,
    })

    const fetchMetrics = async (start, end, silent = false) => {
        if (!silent) setLoading(true)
        const shouldUseInitialRoasRange = !hasLoadedInitialRoasRange.current
        const roasStartDate = shouldUseInitialRoasRange
            ? dayjs().subtract(6, 'day').startOf('day')
            : start
        const roasEndDate = shouldUseInitialRoasRange
            ? dayjs().endOf('day')
            : end
        const params = buildDateParams(start, end)
        const roasParams = buildDateParams(roasStartDate, roasEndDate)

        setRoasChartDateRange({
            startDate: roasStartDate,
            endDate: roasEndDate,
        })
        hasLoadedInitialRoasRange.current = true

        try {
            const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` }
            const [metricsResponse, roasPerDayResponse] = await Promise.allSettled([
                axiosInstance.get('/reports/metrics', {
                    headers,
                    params,
                }),
                axiosInstance.get('/reports/roas-per-day', {
                    headers,
                    params: roasParams,
                }),
            ])

            if (metricsResponse.status === 'fulfilled') {
                setMetrics(metricsResponse.value.data)
            } else {
                console.error('Error fetching metrics:', metricsResponse.reason)
            }

            if (roasPerDayResponse.status === 'fulfilled') {
                setRoasPerDayMetrics({
                    data: roasPerDayResponse.value.data?.roas_data || [],
                    growthPercentage: roasPerDayResponse.value.data?.growth_percentage || 0,
                })
            } else {
                console.error('Error fetching ROAS per day:', roasPerDayResponse.reason)
                setRoasPerDayMetrics({
                    data: [],
                    growthPercentage: 0,
                })
            }
        } catch (error) {
            console.error('Error fetching metrics:', error)
        } finally {
            if (!silent) setLoading(false)
        }
    }

    const handleStartDateChange = (newDate) => {
        setStartDate(dayjs(newDate))
        if (selectedRange !== 'Custom') {
            setSelectedRange('Custom')
        }
    }

    const handleEndDateChange = (newDate) => {
        setEndDate(dayjs(newDate))
        if (selectedRange !== 'Custom') {
            setSelectedRange('Custom')
        }
    }

    const updateDateRange = (range) => {
        setSelectedRange(range)
        switch (range) {
            case 'Today':
                setStartDate(dayjs().startOf('day'))
                setEndDate(dayjs().endOf('day'))
                setPeriod('yesterday')
                break
            case 'Yesterday':
                setStartDate(dayjs().subtract(1, 'day').startOf('day'))
                setEndDate(dayjs().subtract(1, 'day').endOf('day'))
                setPeriod('the day before yesterday')
                break
            case 'This week':
                setStartDate(dayjs().startOf('isoWeek'))
                setEndDate(dayjs().endOf('isoWeek'))
                setPeriod('last week')
                break
            case 'Last week':
                setStartDate(dayjs().subtract(1, 'week').startOf('isoWeek'))
                setEndDate(dayjs().subtract(1, 'week').endOf('isoWeek'))
                setPeriod('the week before last week')
                break
            case 'This month':
                setStartDate(dayjs().startOf('month'))
                setEndDate(dayjs().endOf('month'))
                setPeriod('last month')
                break
            case 'Last month':
                setStartDate(dayjs().subtract(1, 'month').startOf('month'))
                setEndDate(dayjs().subtract(1, 'month').endOf('month'))
                setPeriod('the month before last month')
                break
            case 'This year':
                setStartDate(dayjs().startOf('year'))
                setEndDate(dayjs().endOf('year'))
                setPeriod('last year')
                break
            case 'Last year':
                setStartDate(dayjs().subtract(1, 'year').startOf('year'))
                setEndDate(dayjs().subtract(1, 'year').endOf('year'))
                setPeriod('the year before last year')
                break
            case 'Maximum':
                setStartDate(null)
                setEndDate(null)
                setPeriod(null)
                break
            default:
                setStartDate(dayjs().startOf('day'))
                setEndDate(dayjs().endOf('day'))
        }
    }

    const calculatePercentageChange = (current, previous) => {
        if (!previous) return 0
        return ((current - previous) / previous) * 100
    }

    const brandColor = useColorModeValue("brand.500", "white")
    const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100")
    const loadingBoxBg = useColorModeValue("gray.200", "whiteAlpha.200")

    const formattedProductSales = metrics.productSales?.map(product => ({
        name: [product.name, false],
        totalSales: `${formatNumber(product.totalSales)} RON`,
        quantity: formatNumber(product.orderCount),
        unitsSold: product.unitsSold,
        returnRate: product.returnRate != null
            ? `${formatNumber(product.returnRate)}%`
            : '-',
    }))

    const fetchRoasDetails = async () => {
        setRoasLoading(true)
        try {
            await axiosInstance.get('/roas/details', {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            })
        } catch (error) {
            addErrorNotifications(error.response?.data || error)
        } finally {
            await fetchMetrics(startDate, endDate, true)
            setRoasLoading(false)
        }
    }

    const ch = createColumnHelper();

    const upsellColumns = [
        ch.accessor('upsell_name', {
            id: 'upsell_name',
            header: () => <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">UPSELL</Text>,
            cell: (info) => <Text fontWeight="700">{info.getValue()}</Text>,
        }),
        ch.accessor('order_count', {
            id: 'order_count',
            header: () => <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">ORDERS</Text>,
            cell: (info) => <Text fontWeight="700">{info.getValue()}</Text>,
        }),
    ];

    if (loading) {
        return (
            <Flex justify="center" align="center" height="100vh">
                <Spinner size="xl" />
            </Flex>
        )
    }

    return (
        <Box pt={{ base: "130px", md: "80px", xl: "80px" }} maxWidth="xxl" px={{ base: '20px', md: '40px' }}>

            <SimpleGrid
                columns={{ base: 1, md: 2, lg: 3, "2xl": 6 }}
                gap="20px"
                mb="20px"
            >
                <FormControl>
                    <FormLabel>{tr('dashboard.startDate', 'Start Date')}</FormLabel>
                    <DatePicker
                        selected={startDate?.toDate()}
                        onChange={handleStartDateChange}
                        customInput={<Input />}
                        dateFormat="dd-MM-yyyy"
                    />
                </FormControl>

                <FormControl>
                    <FormLabel>{tr('dashboard.endDate', 'End Date')}</FormLabel>
                    <DatePicker
                        selected={endDate?.toDate()}
                        onChange={handleEndDateChange}
                        customInput={<Input />}
                        dateFormat="dd-MM-yyyy"
                    />
                </FormControl>

                <FormControl>
                    <FormLabel>{tr('dashboard.dateRange', 'Date Range')}</FormLabel>
                    <Select value={selectedRange} onChange={(e) => updateDateRange(e.target.value)}>
                        <option value="Today">{tr('dashboard.ranges.today', 'Today')}</option>
                        <option value="Yesterday">{tr('dashboard.ranges.yesterday', 'Yesterday')}</option>
                        <option value="This week">{tr('dashboard.ranges.thisWeek', 'This Week')}</option>
                        <option value="Last week">{tr('dashboard.ranges.lastWeek', 'Last Week')}</option>
                        <option value="This month">{tr('dashboard.ranges.thisMonth', 'This Month')}</option>
                        <option value="Last month">{tr('dashboard.ranges.lastMonth', 'Last Month')}</option>
                        <option value="This year">{tr('dashboard.ranges.thisYear', 'This Year')}</option>
                        <option value="Last year">{tr('dashboard.ranges.lastYear', 'Last Year')}</option>
                        <option value="Maximum">{tr('dashboard.ranges.maximum', 'Maximum')}</option>
                    </Select>
                </FormControl>
            </SimpleGrid>

            <SimpleGrid
                columns={{ base: 1, md: 2, lg: 3 }}
                gap="20px"
                mb="20px"
            >
                <MiniStatistics
                    startContent={
                        <IconBox
                            w={{ base: "32px", md: "40px" }}
                            h={{ base: "32px", md: "40px" }}
                            bg={boxBg}
                            borderRadius="12px"
                            boxShadow="sm"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            icon={
                                <Icon
                                    as={MdBarChart}
                                    w={{ base: "18px", md: "24px" }}
                                    h={{ base: "18px", md: "24px" }}
                                    color={brandColor}
                                />
                            }
                        />
                    }
                    period={period}
                    name={tr('dashboard.cards.totalSalesOrders', 'Total Sales / Orders')}
                    value={
                        <Box fontSize={{ base: "md", md: "lg" }} mt="6px" whiteSpace="nowrap">
                            <AnimatedNumber value={metrics.totalSalesAll} formatter={v => formatNumber(parseFloat(v.toFixed(2)))} /> RON / <AnimatedNumber value={metrics.totalOrdersAll} formatter={v => formatNumber(Math.round(v))} /> {tr('dashboard.orders', 'orders')}
                        </Box>
                    }
                    growth={calculatePercentageChange(
                        metrics.totalSalesAll,
                        metrics.previousTotalSalesAll
                    )}
                />

                <MiniStatistics
                    startContent={
                        <IconBox
                            w={{ base: "32px", md: "40px" }}
                            h={{ base: "32px", md: "40px" }}
                            bg={boxBg}
                            borderRadius="12px"
                            boxShadow="sm"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            icon={
                                <Icon
                                    as={MdFileCopy}
                                    w={{ base: "18px", md: "24px" }}
                                    h={{ base: "18px", md: "24px" }}
                                    color={brandColor}
                                />
                            }
                        />
                    }
                    period={period}
                    name={tr('dashboard.cards.totalDelivered', 'Total / Orders Delivered')}
                    value={
                        <Box fontSize={{ base: "md", md: "lg" }} mt="6px" whiteSpace="nowrap">
                            <AnimatedNumber value={metrics.totalSales} formatter={v => formatNumber(parseFloat(v.toFixed(2)))} /> RON / <AnimatedNumber value={metrics.orderCount} formatter={v => formatNumber(Math.round(v))} /> {tr('dashboard.orders', 'orders')}
                        </Box>
                    }
                    growth={calculatePercentageChange(
                        metrics.totalSales,
                        metrics.previousTotalSales
                    )}
                />

                <MiniStatistics
                    startContent={
                        <IconBox
                            w={{ base: "32px", md: "40px" }}
                            h={{ base: "32px", md: "40px" }}
                            bg={boxBg}
                            borderRadius="12px"
                            boxShadow="sm"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            icon={
                                <Icon
                                    as={MdAttachMoney}
                                    w={{ base: "18px", md: "24px" }}
                                    h={{ base: "18px", md: "24px" }}
                                    color={brandColor}
                                />
                            }
                        />
                    }
                    period={period}
                    name={tr('dashboard.cards.avgOrderValue', 'Average Order Value')}
                    value={
                        <Box fontSize={{ base: "md", md: "lg" }} mt="6px" whiteSpace="nowrap">
                            {formatNumber(metrics.averageOrderValue)} RON
                        </Box>
                    }
                    growth={calculatePercentageChange(
                        metrics.averageOrderValue,
                        metrics.previousAverageOrderValue
                    )}
                />

                <MiniStatistics
                    startContent={
                        <IconBox
                            w={{ base: "32px", md: "40px" }}
                            h={{ base: "32px", md: "40px" }}
                            bg={boxBg}
                            borderRadius="12px"
                            boxShadow="sm"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            icon={
                                <Icon
                                    as={MdAttachMoney}
                                    w={{ base: "18px", md: "24px" }}
                                    h={{ base: "18px", md: "24px" }}
                                    color={brandColor}
                                />
                            }
                        />
                    }
                    period={period}
                    name={tr('dashboard.cards.spend', 'Spend')}
                    value={
                        <Box fontSize={{ base: "md", md: "lg" }} mt="6px" whiteSpace="nowrap">
                            {formatNumber(metrics.totalSpend)} RON
                        </Box>
                    }
                    growth={calculatePercentageChange(
                        metrics.totalSpend,
                        metrics.previousTotalSpend
                    )}
                />

                <div onDoubleClick={fetchRoasDetails}>
                    <MiniStatistics
                        startContent={
                            <IconBox
                                w={{ base: "32px", md: "40px" }}
                                h={{ base: "32px", md: "40px" }}
                                bg={roasLoading ? loadingBoxBg : boxBg}
                                borderRadius="12px"
                                boxShadow="sm"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                icon={
                                    roasLoading ? (
                                        <Spinner size="sm" />
                                    ) : (
                                        <Icon
                                            as={MdTrendingUp}
                                            w={{ base: "18px", md: "24px" }}
                                            h={{ base: "18px", md: "24px" }}
                                            color={brandColor}
                                        />
                                    )
                                }
                            />
                        }
                        period={period}
                    name={tr('dashboard.cards.roas', 'ROAS')}
                        value={
                            <Box fontSize={{ base: "md", md: "lg" }} mt="6px" whiteSpace="nowrap">
                                {formatNumber(metrics.roas)}
                            </Box>
                        }
                        roasTime={metrics.last_roas_time}
                        warning={metrics.meta_token_invalid ? tr('dashboard.cards.metaTokenExpired', 'Your Meta access token has expired. ROAS data will stop updating until it is replaced.') : null}
                        growth={calculatePercentageChange(
                            metrics.roas,
                            metrics.previousRoas
                        )}
                    />
                </div>

                <MiniStatistics
                    startContent={
                        <IconBox
                            w={{ base: "32px", md: "40px" }}
                            h={{ base: "32px", md: "40px" }}
                            bg={boxBg}
                            borderRadius="12px"
                            boxShadow="sm"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            icon={
                                <Icon
                                    as={MdSwapHoriz}
                                    w={{ base: "18px", md: "24px" }}
                                    h={{ base: "18px", md: "24px" }}
                                    color={brandColor}
                                />
                            }
                        />
                    }
                    period={period}
                    name={tr('dashboard.cards.conversionRate', 'Conversion Rate')}
                    value={
                        <Box fontSize={{ base: "md", md: "lg" }} mt="6px" whiteSpace="nowrap">
                            {formatNumber(metrics.conversionRate)}%
                        </Box>
                    }
                    growth={calculatePercentageChange(
                        metrics.conversionRate,
                        metrics.previousConversionRate
                    )}
                />
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 1, xl: 2 }} gap="20px" mb="20px">
                <CheckTable
                    columnsData={[
                        { Header: tr('dashboard.table.productName', 'Product Name'), accessor: "name" },
                        { Header: tr('dashboard.table.totalSales', 'Total Sales'), accessor: "totalSales" },
                        { Header: tr('dashboard.table.totalOrders', 'Total Orders'), accessor: "orderCount" },
                        { Header: tr('dashboard.table.totalUnits', 'Total Units Sold'), accessor: "unitsSold" },
                        { Header: tr('dashboard.table.returnRate', 'Return %'), accessor: "returnRate" },
                    ]}
                    tableData={formattedProductSales || []}
                />
                <DailyTraffic />
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 1, xl: 2 }} gap="20px" mb="20px">
                <TotalSpent/>
                <TrafficByProduct productTraffic={metrics.productTraffic || []} />
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 1, xl: 2 }} gap="20px" mb="20px">
                <PieCard data={metrics.orderSummary || []}/>
                <BestSellingOptionsChart productSales={metrics.productSales || []}/>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 1, xl: 2 }} gap="20px" mb="20px">
                {
                    Array.isArray(metrics?.upsells) && metrics.upsells.length > 0 &&
                    <UpsellsList
                        data={metrics.upsells || []}
                        title={tr('dashboard.upsells', 'Upsells')}
                        maxHeight="420px"
                    />
                }
                <RoasPerDay
                    data={roasPerDayMetrics.data}
                    growthPercentage={roasPerDayMetrics.growthPercentage}
                    startDate={roasChartDateRange.startDate}
                    endDate={roasChartDateRange.endDate}
                />
            </SimpleGrid>
        </Box>
    )
}

export default Dashboard
